<template>
  <section
    class="relative cursor-pointer rounded-md bg-theme-background text-theme-text shadow-[rgba(var(--color-theme-text),0.2)_5px_5px_20px_0px] transition"
    @click="previewingProductStore.setPreviewingProduct(product)"
  >
    <div
      v-if="getSharedData().isOrderMode"
      class="pointer-events-none absolute bottom-0.5 left-0.5 right-0.5 flex justify-end"
    >
      <div
        class="z-10 flex h-10 w-10 items-center justify-center rounded-full border-[3px] border-theme-background bg-theme-primary text-theme-button-text"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            id="icon-basket"
            d="M13.7428 5.5791C14.3859 5.5791 14.8618 6.17744 14.7171 6.8041L13.4444 12.3156C13.3717 12.7983 13.1762 13.2311 12.892 13.5388C12.6078 13.8466 12.2528 14.0097 11.8889 13.9997H4.11111C3.7472 14.0097 3.39223 13.8466 3.108 13.5388C2.82377 13.2311 2.62828 12.7983 2.55556 12.3156L1.28287 6.80409C1.13817 6.17744 1.61409 5.5791 2.25723 5.5791H13.7428Z"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          ></path>
          <path
            d="M3.625 5.57895L5.8125 2M12.375 5.57895L10.1875 2"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
          ></path>
          <path
            d="M6.25 8.94727L6.25 10.6315"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
          ></path>
          <path
            d="M9.75 8.94727L9.75 10.6315"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
          ></path>
        </svg>
      </div>
    </div>
    <div
      v-if="isFirst && tapForDetailsStore.isTapForDetailsVisible"
      class="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-md bg-theme-background/80 p-3 text-theme-text"
    >
      <HandTapRegular
        class="block h-10 w-10 animate-[popping_ease-in_1s_infinite]"
      />

      <div class="mt-2 max-w-full break-words text-center font-semibold">
        {{ trans('Tap for details') }}
      </div>
    </div>

    <div
      class="flex h-full"
      :class="{
        'flex-col':
          displayType === DisplayType.GRID ||
          displayType === DisplayType.GRID_EXTENDED,
        'flex-row-reverse': displayType === DisplayType.LIST,
      }"
    >
      <div
        v-if="
          displayType === DisplayType.GRID ||
          displayType === DisplayType.GRID_EXTENDED ||
          productImageThumbnailUrl ||
          defaultProductImageUrl
        "
        class="flex-shrink-0"
        :class="{
          'w-full':
            displayType === DisplayType.GRID ||
            displayType === DisplayType.GRID_EXTENDED,
          'w-32 p-3': displayType === DisplayType.LIST,
        }"
      >
        <div
          class="relative"
          :class="
            displayType === DisplayType.GRID_EXTENDED ? 'pb-[70%]' : 'pb-[100%]'
          "
        >
          <AImage
            class="absolute inset-0 h-full w-full overflow-hidden object-cover object-center"
            :class="{
              'rounded-tl-lg rounded-tr-lg':
                displayType === DisplayType.GRID ||
                displayType === DisplayType.GRID_EXTENDED,
              'rounded-md': displayType === DisplayType.LIST,
            }"
            :src="
              productImageThumbnailUrl ||
              defaultProductImageUrl ||
              restaurant.fallbackProductImage.url
            "
            :alt="localize(product.name) ?? ''"
          />
        </div>
      </div>

      <div
        class="relative flex min-w-0 flex-col p-3"
        :class="{ 'flex-1': displayType === DisplayType.LIST }"
      >
        <h3
          class="shrink-0 break-words font-primary font-medium leading-tight"
          :class="{
            'text-xl': displayType === DisplayType.GRID_EXTENDED,
          }"
        >
          {{ localize(product.name) }}
        </h3>
        <div
          v-if="product.ingredientWarnings.length > 0"
          class="mt-0.5 text-xs italic text-theme-text/60"
        >
          {{
            product.ingredientWarnings
              .map((w) => localize(w.title, { fallbackToEn: true }))
              .join(', ')
          }}
        </div>
        <div class="mt-2 flex-1 shrink-0" style="min-height: 40px">
          <div
            class="line-clamp-2 whitespace-pre-line break-words text-sm text-theme-text/80"
          >
            {{ localize(product.plainDescription) }}
          </div>
        </div>
        <div class="mt-2 flex shrink-0 items-center justify-between">
          <div class="flex flex-wrap items-baseline text-sm">
            <span
              v-if="sortedPricedVariants.length > 0"
              class="whitespace-nowrap font-semibold text-theme-text ltr:mr-1.5 rtl:ml-1.5"
            >
              {{ formatPrice(sortedPricedVariants[0].price) }}
            </span>
            <template
              v-if="
                sortedPricedVariants.length > 1 &&
                sortedPricedVariants[0].price !==
                  sortedPricedVariants[sortedPricedVariants.length - 1].price
              "
            >
              <span class="ltr:mr-1.5 rtl:ml-1.5">-</span>
              <span
                class="whitespace-nowrap font-semibold text-theme-text ltr:mr-1.5 rtl:ml-1.5"
              >
                {{
                  formatPrice(
                    sortedPricedVariants[sortedPricedVariants.length - 1].price,
                  )
                }}
              </span>
            </template>
            <span
              v-if="
                sortedPricedVariants.length === 1 &&
                sortedPricedVariants[0].oldPrice !== null &&
                sortedPricedVariants[0].oldPrice > 0
              "
              class="whitespace-nowrap text-[0.9em] font-medium text-theme-text/45 line-through ltr:mr-1.5 rtl:ml-1.5"
            >
              {{ formatPrice(sortedPricedVariants[0].oldPrice) }}
            </span>
          </div>
        </div>
        <ProductBadges :product="product" class="mt-2 shrink-0 empty:hidden" />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import ProductBadges from '@menu/Shared/ProductBadges.vue';
import AImage from '@menu/Shared/AImage.vue';
import { usePreviewingProductStore } from '@menu/Stores/previewingProduct';
import { localize, trans } from '@menu/Utils/i18n';
import formatPrice from '@menu/Utils/formatPrice';
import getSharedData from '@menu/Utils/getSharedData';
import HandTapRegular from '@/phosphoricons/HandTapRegular.vue';
import { useTapForDetailsStore } from '@menu/Stores/tapForDetails';
import { ProductResource } from '@menu/Types/Resources/ProductResource';
import { DisplayType } from '@menu/Types/Enums/DisplayType';
import { useCurrentMenu } from '@menu/Composables/useCurrentMenu';
import { computed, onMounted, onUnmounted } from 'vue';
import getRestaurant from '@menu/Utils/getRestaurant';

const props = defineProps<{
  product: ProductResource;
  displayType: DisplayType;
  isFirst?: boolean;
}>();

const previewingProductStore = usePreviewingProductStore();
const tapForDetailsStore = useTapForDetailsStore();

const restaurant = getRestaurant();

const { menu } = useCurrentMenu();

const productImageThumbnailUrl = computed(() => {
  if (!props.product.image) {
    return null;
  }

  // Grid extended requires better image quality.
  if (props.displayType === DisplayType.GRID_EXTENDED) {
    return props.product.image.url;
  }

  return props.product.image.thumbnailUrl;
});

const sortedPricedVariants = computed(() => {
  const pricedVariants = props.product.variants.filter(
    (
      variant,
    ): variant is (typeof props.product.variants)[number] & {
      price: number;
    } => variant.price !== null && variant.price > 0,
  );

  return [...pricedVariants].sort((firstVariant, secondVariant) => {
    return firstVariant.price - secondVariant.price;
  });
});

const defaultProductImageUrl = computed(() => {
  return menu.value.defaultProductImage
    ? menu.value.defaultProductImage.url
    : null;
});

onMounted(() => {
  if (props.isFirst && tapForDetailsStore.isTapForDetailsVisible) {
    document.addEventListener('click', hideTapForDetailsOnDocumentClick);
  }
});

onUnmounted(() => {
  document.removeEventListener('click', hideTapForDetailsOnDocumentClick);
});

const hideTapForDetailsOnDocumentClick = () => {
  tapForDetailsStore.hideTapForDetails();

  document.removeEventListener('click', hideTapForDetailsOnDocumentClick);
};
</script>
